import React from 'react'

// Inline editor for job segments. Used from NewJobModal, JobCard, and the bid flow.
// `segments` is an array of { segment_number, start_date, end_date, description, crew_size_needed, status }.
// `validation` is the result of lib/segments validateSegments.
export default function SegmentsEditor({ segments, onChange, validation, defaultCrewSize }) {
  const rows = segments || []

  const updateRow = (idx, patch) => {
    const next = rows.map((r, i) => (i === idx ? { ...r, ...patch } : r))
    onChange(renumber(next))
  }

  const removeRow = (idx) => {
    const next = rows.filter((_, i) => i !== idx)
    onChange(renumber(next.length > 0 ? next : [blankRow(defaultCrewSize)]))
  }

  const addRow = () => {
    const last = rows[rows.length - 1]
    const nextDate = last?.end_date || last?.start_date || ''
    onChange(renumber(rows.concat([{ segment_number: rows.length + 1, start_date: nextDate, end_date: nextDate, description: '', crew_size_needed: defaultCrewSize || '' }])))
  }

  const errorFor = (i) => (validation?.overlapErrors || []).find(e => e.idx === i)?.msg

  return (
    <div style={styles.wrap}>
      <div style={styles.header}>
        <div style={styles.headLabel}>WORK SCHEDULE · {rows.length} VISIT{rows.length === 1 ? '' : 'S'}</div>
        <button type="button" onClick={addRow} style={styles.addBtn}>+ ADD VISIT</button>
      </div>
      {rows.map((seg, i) => {
        const rowErr = errorFor(i)
        return (
          <div key={i} style={{ ...styles.row, borderLeft: '3px solid ' + (rowErr ? '#e05c5c' : '#4a8c6a') }}>
            <div style={styles.rowHead}>
              <div style={styles.visitTag}>VISIT {i + 1}</div>
              <button type="button" onClick={() => removeRow(i)} style={styles.removeBtn}>REMOVE</button>
            </div>
            <div style={styles.grid2}>
              <div style={styles.field}><label style={styles.label}>START</label><input type="date" value={seg.start_date || ''} onChange={e => updateRow(i, { start_date: e.target.value })} style={styles.input} /></div>
              <div style={styles.field}><label style={styles.label}>END</label><input type="date" value={seg.end_date || ''} onChange={e => updateRow(i, { end_date: e.target.value })} style={styles.input} /></div>
              <div style={{ ...styles.field, gridColumn: '1 / -1' }}><label style={styles.label}>DESCRIPTION</label><input value={seg.description || ''} onChange={e => updateRow(i, { description: e.target.value })} style={styles.input} placeholder="e.g. Initial clearing, Return for stump grinding" /></div>
              <div style={styles.field}><label style={styles.label}>CREW SIZE</label><input type="number" min="1" max="15" value={seg.crew_size_needed || ''} onChange={e => updateRow(i, { crew_size_needed: e.target.value })} style={styles.input} placeholder={String(defaultCrewSize || 3)} /></div>
              {seg.status && seg.status !== 'scheduled' && (
                <div style={styles.field}><label style={styles.label}>STATUS</label><div style={{ ...styles.input, display: 'flex', alignItems: 'center', color: seg.status === 'completed' ? '#4a8c6a' : '#b48c3c' }}>{seg.status.replace('_', ' ').toUpperCase()}</div></div>
              )}
            </div>
            {rowErr && <div style={styles.rowError}>{rowErr}</div>}
          </div>
        )
      })}
      {validation?.startError && <div style={styles.error}>{validation.startError}</div>}
    </div>
  )
}

function blankRow(defaultCrewSize) {
  return { segment_number: 1, start_date: '', end_date: '', description: '', crew_size_needed: defaultCrewSize || '' }
}

function renumber(rows) {
  return rows.map((r, i) => ({ ...r, segment_number: i + 1 }))
}

const styles = {
  wrap: { marginBottom: '14px' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' },
  headLabel: { fontFamily: "'Barlow Condensed',sans-serif", fontSize: '10px', letterSpacing: '2px', color: 'rgba(255,255,255,0.4)' },
  addBtn: { padding: '6px 12px', backgroundColor: 'rgba(74,140,106,0.15)', border: '1px solid rgba(74,140,106,0.3)', borderRadius: '3px', color: '#4a8c6a', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '11px', fontWeight: 700, letterSpacing: '1.5px', cursor: 'pointer' },
  row: { padding: '10px 12px', backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '3px', marginBottom: '8px' },
  rowHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' },
  visitTag: { fontFamily: "'Barlow Condensed',sans-serif", fontSize: '10px', letterSpacing: '2px', color: '#4a8c6a', fontWeight: 700 },
  removeBtn: { padding: '3px 8px', backgroundColor: 'transparent', border: '1px solid rgba(224,92,92,0.3)', borderRadius: '2px', color: '#e05c5c', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '10px', letterSpacing: '1px', cursor: 'pointer' },
  grid2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' },
  field: { display: 'flex', flexDirection: 'column', gap: '4px' },
  label: { fontFamily: "'Barlow Condensed',sans-serif", fontSize: '10px', letterSpacing: '2px', color: 'rgba(255,255,255,0.35)' },
  input: { backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '3px', padding: '8px 10px', color: '#ffffff', fontSize: '14px', fontFamily: "'Barlow',sans-serif", outline: 'none', width: '100%', boxSizing: 'border-box' },
  rowError: { marginTop: '6px', fontSize: '12px', color: '#e05c5c' },
  error: { marginTop: '6px', fontSize: '13px', color: '#e05c5c' },
}
